import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { TextareaAutosize } from '@mui/material';
import { type RootState } from '../../context/store';
import { setElementsContent, setUnsavedChanges } from '../../context/reducers/editor';
import { EDITOR_NOTES } from '../../constants/editorKeys';
import { checkIfPostHasEditorNotes } from '../../utils/wysiwyg';
import POSTS_STATUS from '../../constants/status';

interface Props {
  currentStatus: string | null;
}

interface ContainerI {
  isPreviewModeActive: boolean;
}

export default function EditorNotes({ currentStatus }: Props) {
  const elementsContent = useSelector((state: RootState) => state.editor.elementsContent);
  const isPreviewModeActive = useSelector((state: RootState) => state.toolBar.isPreviewModeActive);
  const [editorNotes, setEditorNotes] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    const content =
      elementsContent[elementsContent.findIndex((item) => item.customKey === EDITOR_NOTES)].content;
    setEditorNotes(typeof content === 'string' ? content : '');
  }, []);

  useEffect(() => {
    dispatch(
      setElementsContent({
        customKey: EDITOR_NOTES,
        content: editorNotes.trim(),
      }),
    );
  }, [editorNotes]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(
      setUnsavedChanges({
        unsavedChanges: true,
      }),
    );
    setEditorNotes(e.target.value);
  };

  return (
    <>
      {currentStatus === POSTS_STATUS.REVIEW_IN_PROGRESS ? (
        <Container isPreviewModeActive={isPreviewModeActive}>
          <Content>
            <Title>Notas del Editor</Title>
            <TextArea
              disabled={currentStatus == null || currentStatus !== POSTS_STATUS.REVIEW_IN_PROGRESS}
              value={editorNotes}
              onChange={handleChange}
              style={{ width: '920px', resize: 'none' }}
              placeholder='Escribe aquí...'
            />
          </Content>
        </Container>
      ) : (
        checkIfPostHasEditorNotes(elementsContent) &&
        currentStatus !== POSTS_STATUS.PUBLISHED && (
          <Container isPreviewModeActive={isPreviewModeActive}>
            <Content>
              <Title>Notas del Editor</Title>
              <TextArea
                disabled={
                  currentStatus == null || currentStatus !== POSTS_STATUS.REVIEW_IN_PROGRESS
                }
                value={editorNotes}
                onChange={handleChange}
                style={{ width: '920px', resize: 'none' }}
                placeholder='Escribe aquí...'
              />
            </Content>
          </Container>
        )
      )}
    </>
  );
}

interface ContainerI {
  style?: React.CSSProperties;
}

const Container = React.forwardRef<HTMLDivElement, ContainerI & React.PropsWithChildren<{ isPreviewModeActive: boolean } & React.HTMLAttributes<HTMLDivElement>>>(
  ({ isPreviewModeActive, children, style, ...rest }, ref) => (
    <div
      ref={ref}
      style={{
        paddingBottom: 100,
        borderTop: '1px dashed #e0e0e0', // fallback color
        marginTop: 25,
        paddingTop: 70,
        marginBottom: 200,
        ...(isPreviewModeActive
          ? { height: 10, paddingBottom: 0, overflow: 'hidden', borderTop: 'none' }
          : {}),
        ...(style || {}),
      }}
      {...rest}
    >
      {children}
    </div>
  )
);

Container.displayName = "Container";

const Content = styled.div`
  width: 920px;
  margin: 0px auto;
`;

const Title = styled.div`
  font-family: ${(props) => props.theme.fonts.regular};
  font-size: 20px;
  color: ${(props) => props.theme.colors.accentBlue};
`;

const TextArea = styled(TextareaAutosize)`
  margin-top: 20px;
  min-height: 120px;
  background-color: transparent;
  border: none;
  outline: none;
  font-family: ${(props) => props.theme.fonts.regular};
  color: ${(props) => props.theme.colors.accentBlue};
  font-size: 18px;
  ::placeholder {
    color: ${(props) => props.theme.colors.accentBlue};
  }
`;
